import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, Lock, ArrowLeft, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { LogoMark } from "@/components/LogoMark";

export default function AdminLogin() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login"); // login | forgot | reset
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) navigate("/admin");
  }, [user, navigate]);

  const inputCls =
    "w-full bg-surface border border-surface-2 px-4 py-3 text-white placeholder:text-zinc-600 font-sans focus:outline-none focus:border-hazard focus:ring-1 focus:ring-hazard transition-colors";

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/admin");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const requestCode = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email });
      toast.success("If that account exists, a reset code has been emailed to it.");
      setMode("reset");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const doReset = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { email, code, new_password: newPassword });
      toast.success("Password reset! Sign in with your new password.");
      setMode("login");
      setPassword("");
      setCode("");
      setNewPassword("");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-ink flex flex-col">
      <div className="hazard-stripes h-3 w-full" />
      <div className="flex-1 flex items-center justify-center px-5">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md">
          <a
            href="/"
            data-testid="back-home-link"
            className="inline-flex items-center gap-2 text-zinc-500 hover:text-hazard font-mono text-xs tracking-[0.2em] uppercase mb-10 transition-colors"
          >
            <ArrowLeft size={14} /> Back to site
          </a>
          <LogoMark size="lg" className="mb-2" />

          {mode === "login" && (
            <>
              <h1 className="font-display uppercase text-5xl mt-8 mb-2 flex items-center gap-3">
                <Lock size={32} className="text-hazard" /> Staff Login
              </h1>
              <p className="text-zinc-500 mb-8 font-mono text-xs tracking-[0.15em] uppercase">
                Bookings dashboard access
              </p>
              <form onSubmit={submit} className="space-y-5">
                <div>
                  <label className="font-mono text-xs tracking-[0.2em] uppercase text-zinc-500 block mb-2">Email</label>
                  <input type="email" data-testid="login-email" value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} required />
                </div>
                <div>
                  <label className="font-mono text-xs tracking-[0.2em] uppercase text-zinc-500 block mb-2">Password</label>
                  <input type="password" data-testid="login-password" value={password} onChange={(e) => setPassword(e.target.value)} className={inputCls} required />
                </div>
                {error && <p data-testid="login-error" className="text-destructive font-mono text-xs tracking-wide">{error}</p>}
                <button type="submit" data-testid="login-submit-btn" disabled={loading} className="w-full flex items-center justify-center gap-2 bg-hazard text-black py-3.5 font-mono text-sm tracking-[0.15em] uppercase font-semibold hover:bg-white transition-colors disabled:opacity-40">
                  {loading ? <Loader2 size={18} className="animate-spin" /> : "Sign In"}
                </button>
              </form>
              <button
                type="button"
                data-testid="forgot-password-link"
                onClick={() => { setMode("forgot"); setError(""); }}
                className="mt-6 text-zinc-500 hover:text-hazard font-mono text-xs tracking-[0.15em] uppercase transition-colors"
              >
                Forgot password?
              </button>
            </>
          )}

          {mode === "forgot" && (
            <>
              <h1 className="font-display uppercase text-4xl mt-8 mb-2 flex items-center gap-3">
                <KeyRound size={28} className="text-hazard" /> Reset Password
              </h1>
              <p className="text-zinc-500 mb-8 font-mono text-xs tracking-[0.15em] uppercase">
                We'll email a 4-digit code to your account
              </p>
              <form onSubmit={requestCode} className="space-y-5">
                <div>
                  <label className="font-mono text-xs tracking-[0.2em] uppercase text-zinc-500 block mb-2">Account Email</label>
                  <input type="email" data-testid="forgot-email" value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} required />
                </div>
                {error && <p data-testid="forgot-error" className="text-destructive font-mono text-xs tracking-wide">{error}</p>}
                <button type="submit" data-testid="send-code-btn" disabled={loading} className="w-full flex items-center justify-center gap-2 bg-hazard text-black py-3.5 font-mono text-sm tracking-[0.15em] uppercase font-semibold hover:bg-white transition-colors disabled:opacity-40">
                  {loading ? <Loader2 size={18} className="animate-spin" /> : "Send Reset Code"}
                </button>
              </form>
              <div className="mt-6 flex items-center gap-6">
                <button type="button" onClick={() => { setMode("login"); setError(""); }} className="text-zinc-500 hover:text-hazard font-mono text-xs tracking-[0.15em] uppercase transition-colors">
                  Back to login
                </button>
                <button type="button" data-testid="have-code-link" onClick={() => { setMode("reset"); setError(""); }} className="text-zinc-500 hover:text-hazard font-mono text-xs tracking-[0.15em] uppercase transition-colors">
                  I already have a code
                </button>
              </div>
            </>
          )}

          {mode === "reset" && (
            <>
              <h1 className="font-display uppercase text-4xl mt-8 mb-2 flex items-center gap-3">
                <KeyRound size={28} className="text-hazard" /> Enter Code
              </h1>
              <p className="text-zinc-500 mb-8 font-mono text-xs tracking-[0.15em] uppercase">
                Enter the 4-digit code and your new password
              </p>
              <form onSubmit={doReset} className="space-y-5">
                <div>
                  <label className="font-mono text-xs tracking-[0.2em] uppercase text-zinc-500 block mb-2">Account Email</label>
                  <input type="email" data-testid="reset-email" value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} required />
                </div>
                <div>
                  <label className="font-mono text-xs tracking-[0.2em] uppercase text-zinc-500 block mb-2">4-Digit Code</label>
                  <input type="text" inputMode="numeric" maxLength={4} data-testid="reset-code" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} className={`${inputCls} tracking-[0.6em] text-center text-2xl`} required />
                </div>
                <div>
                  <label className="font-mono text-xs tracking-[0.2em] uppercase text-zinc-500 block mb-2">New Password (min 8 chars)</label>
                  <input type="password" data-testid="reset-new-password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} className={inputCls} minLength={8} required />
                </div>
                {error && <p data-testid="reset-error" className="text-destructive font-mono text-xs tracking-wide">{error}</p>}
                <button type="submit" data-testid="reset-submit-btn" disabled={loading} className="w-full flex items-center justify-center gap-2 bg-hazard text-black py-3.5 font-mono text-sm tracking-[0.15em] uppercase font-semibold hover:bg-white transition-colors disabled:opacity-40">
                  {loading ? <Loader2 size={18} className="animate-spin" /> : "Reset Password"}
                </button>
              </form>
              <button type="button" onClick={() => { setMode("login"); setError(""); }} className="mt-6 text-zinc-500 hover:text-hazard font-mono text-xs tracking-[0.15em] uppercase transition-colors">
                Back to login
              </button>
            </>
          )}
        </motion.div>
      </div>
    </div>
  );
}
